import React, { useEffect, useState } from 'react';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import MultipleFileUpload from 'app/shared/file-upload/multiple-file-upload';
import { useFormik } from 'formik';
import { Button } from 'edozvola-frontend-lib/dist/ui/components';
import { uploadFilesDefaultValidationSchema } from 'app/shared/file-upload/model/file-upload.schema';
import { FileUploadModel } from 'app/shared/file-upload/model/file-upload.model';
import { showToast } from 'app/shared/util/toast.util';
import { toast } from 'react-toastify';
import { queryClient } from 'app/app';
import {
  uploadMainProjectDocumentAttachmentQuery
} from 'app/edozvola/cases/case-main-project-documents/service/case-main-project-documents.queries';
import MainProjectDocumentAttachmentsTable
  from 'app/edozvola/cases/case-main-project-documents/attachment-modal/main-project-document-attachments-table';
import { TABLE_QUERY_KEYS } from 'app/config/constants';
import error = toast.error;
import success = toast.success;

export type AttachmentModalVariant = 'ADD' | 'VIEW';

export interface IAttachmentModalProps {
  idMainProjectDocument: string | null;
  setIdMainProjectDocument: any;
  type: AttachmentModalVariant;
}

const MainProjectDocumentAttachmentModal = (props: IAttachmentModalProps) => {
  const [numbOfFilesLeftToUpload, setNumOfFilesLeftToUpload] = useState(-1);
  const [failedFiles, setFailedFiles] = useState<string[]>([]);

  useEffect(() => {
    if (numbOfFilesLeftToUpload === 0) {
      closeModal();
      showToasts();
    }
  }, [numbOfFilesLeftToUpload]);

  const fileUploadedSuccessfully = (file: FileUploadModel) => {
    setNumOfFilesLeftToUpload(prevState => prevState - 1);
  };

  const fileFailedToUpload = (data: string, file: FileUploadModel) => {
    setFailedFiles(prevState => {
      const newState = prevState;
      newState.push(file.fileName);
      return newState;
    });
    setNumOfFilesLeftToUpload(prevState => prevState - 1);
    if (data.startsWith('Datoteka ')) {
      showToast(toast.error, data);
    }
  };

  const { mutate: uploadAttachment, isPending: isAttachmentUploading } = uploadMainProjectDocumentAttachmentQuery(
    props.idMainProjectDocument,
    fileUploadedSuccessfully,
    fileFailedToUpload,
  );

  const filesFormik = useFormik({
    initialValues: { files: [] as FileUploadModel[] },
    validationSchema: uploadFilesDefaultValidationSchema,
    onSubmit: values => uploadFiles(values.files),
  });

  const showToasts = () => {
    if (failedFiles.length > 0) {
      showToast(error, `Dio priloga nije uspješno učitan: ( ${failedFiles.join(', ')} ).`);
    } else {
      queryClient.invalidateQueries({ queryKey: ['main-project-document-attachments-table'] });
      queryClient.invalidateQueries({ queryKey: [TABLE_QUERY_KEYS.MAIN_PROJECT_DOCUMENTS_TABLE] });
      showToast(success, 'Prilozi su uspješno učitani.');
    }
  };

  const closeModal = () => {
    filesFormik.resetForm();
    setFailedFiles([]);
    setNumOfFilesLeftToUpload(-1);
    props.setIdMainProjectDocument(null);
  };

  const uploadFiles = (files: FileUploadModel[]) => {
    setNumOfFilesLeftToUpload(files.length);
    files.forEach(file => uploadAttachment(file));
  };

  return (
    <Modal scrollable isOpen={props.idMainProjectDocument !== null} centered size={'xl'}>
      <ModalHeader toggle={props.type === 'VIEW' ? closeModal : null}>
        {props.type === 'ADD' ? 'Unos priloga na dijelu projekta' : 'Pregled dodanih priloga na dijelu projekta'}
      </ModalHeader>
      <ModalBody>
        {props.idMainProjectDocument && props.type === 'ADD' && (
          <MultipleFileUpload formik={filesFormik} showLoader={numbOfFilesLeftToUpload > 0} />
        )}
        {props.idMainProjectDocument && props.type === 'VIEW' && (
          <>
            <MainProjectDocumentAttachmentsTable idMainProjectDocument={props.idMainProjectDocument} />
          </>
        )}
      </ModalBody>
      <ModalFooter>
        {props.type === 'ADD' && (
          <>
            <Button size={'lg'} label={'Odustani'} outline color={'secondary'} onClick={closeModal} />
            <Button
              size={'lg'}
              label={'Spremi'}
              isLoading={isAttachmentUploading}
              color={'info'}
              onClick={() => filesFormik.handleSubmit()}
              disabled={!filesFormik.dirty || !filesFormik.isValid}
            />
          </>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default MainProjectDocumentAttachmentModal;
